import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from 'react';

// 태그 검증 결과 타입
export type TagValidationResult = 'duplicate' | 'invalid' | null;

export interface UseTagInputOptions {
  value: string[];
  onChange: (tags: string[]) => void;
  // 태그 추가 전 검증 함수 (null 반환 시 통과)
  validateTag?: (tag: string, currentTags: string[]) => TagValidationResult;
  // 태그 추가 시 변환 함수 (null 반환 시 추가하지 않음)
  transformOnAdd?: (tag: string) => string | null;
  // validation 에러 발생 시 콜백
  onValidationError?: (result: TagValidationResult, tag: string) => void;
  /** 입력값 필터 (false 반환 시 입력 차단, 기본: 공백 차단) */
  inputFilter?: (value: string) => boolean;
}

const DEFAULT_INPUT_FILTER = (value: string) => !value.includes(' ');

export function useTagInput({
  value,
  onChange,
  validateTag,
  transformOnAdd,
  onValidationError,
  inputFilter,
}: UseTagInputOptions) {
  const [inputValue, setInputValue] = useState('');

  // stale closure 방지를 위해 ref로 최신 value 참조
  const valueRef = useRef(value);
  valueRef.current = value;

  // 태그 추가 처리
  const addTag = useCallback(
    (rawTag: string) => {
      const trimmedTag = rawTag.trim();
      if (!trimmedTag) return;

      // 변환 적용
      const transformedTag = transformOnAdd
        ? transformOnAdd(trimmedTag)
        : trimmedTag;
      if (transformedTag === null) return;

      // ref를 사용하여 항상 최신 value 참조 (stale closure 방지)
      const currentValue = valueRef.current;

      // 검증 수행
      const validationResult = validateTag
        ? validateTag(trimmedTag, currentValue)
        : null;

      if (validationResult) {
        onValidationError?.(validationResult, trimmedTag);
        return;
      }

      // 중복 제거 후 추가
      const newTags = [...new Set([...currentValue, transformedTag])];
      onChange(newTags);
      setInputValue('');
    },
    [onChange, validateTag, transformOnAdd, onValidationError],
  );

  // 태그 삭제 처리
  const removeTag = useCallback(
    (index: number) => {
      const currentValue = valueRef.current;
      const newTags = [...currentValue];
      newTags.splice(index, 1);
      onChange(newTags);
    },
    [onChange],
  );

  // 마지막 태그 삭제
  const removeLastTag = useCallback(() => {
    const currentValue = valueRef.current;
    if (currentValue.length > 0) {
      removeTag(currentValue.length - 1);
    }
  }, [removeTag]);

  // 입력값 변경 핸들러 (기본: 공백 차단)
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const filter = inputFilter ?? DEFAULT_INPUT_FILTER;
    if (!filter(newValue)) return;
    setInputValue(newValue);
  }, [inputFilter]);

  // 키보드 이벤트 핸들러
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // 키 반복 입력 무시 (키를 누르고 있는 경우 중복 추가 방지)
      if (e.repeat) return;
      // IME 입력 중이면 무시 (한국어 등 입력 시 Enter가 두 번 처리되는 문제 방지)
      // keyCode 229는 IME에서 처리 중인 키를 나타냄
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && inputValue === '') {
        e.preventDefault();
        removeLastTag();
      } else if (e.key === ' ') {
        const filter = inputFilter ?? DEFAULT_INPUT_FILTER;
        if (!filter(' ')) {
          e.preventDefault();
        }
      }
    },
    [inputValue, addTag, removeLastTag, inputFilter],
  );

  return {
    inputValue,
    setInputValue,
    addTag,
    removeTag,
    removeLastTag,
    handleInputChange,
    handleKeyDown,
  };
}
